import styles from '../../css/header.pcss.json'

const lightStyles = document.querySelectorAll<HTMLLinkElement>('link[rel=stylesheet][data-color-scheme-light]')
const darkStyles = document.querySelectorAll<HTMLLinkElement>('link[rel=stylesheet][data-color-scheme-dark]')
const button = document.querySelector<HTMLButtonElement & {
  colorSchemeAttached?: boolean
}>('[data-color-scheme-button]')
const dropdown = document.querySelector<HTMLDivElement>('[data-color-scheme-dropdown]')
const darkSchemeMedia = window.matchMedia('(prefers-color-scheme: dark)')
const storageKey = 'sg-color-scheme'
let prevScheme = 'auto'

let localStorageProxy = ((): Storage => {
  const dummyStorage = {
    length: 0,
    clear: () => {},
    getItem: () => null,
    setItem: () => null,
    removeItem: () => null,
    key: () => null,
  }
  try {
    const storage = window.localStorage

    if (!storage) {
      return dummyStorage
    }

    // Trying to access localStorage throws an exception in Safari Private Browsing mode
    storage.getItem('test')

    return storage
  } catch (e) {
    return dummyStorage
  }
})()

export function setCookie(name: string, value: string, options: {
  path?: string,
  expires?: string|Date,
  'max-age'?: number,
  SameSite?: 'Strict'|'Lax'
} = {}) {

  options = {
    path: '/',
    SameSite: 'Strict',
    ...options
  };

  if (options.expires instanceof Date) {
    options.expires = options.expires.toUTCString();
  }

  let updatedCookie = encodeURIComponent(name) + "=" + encodeURIComponent(value);

  for (let optionKey in options) {
    updatedCookie += "; " + optionKey;
    let optionValue = options[optionKey];
    if (optionValue !== true) {
      updatedCookie += "=" + optionValue;
    }
  }

  document.cookie = updatedCookie;
}

function getCookie(name: string) {
  let matches = document.cookie.match(new RegExp(
    "(?:^|; )" + name.replace(/([.$?*|{}()[]\\\/\+^])/g, '\\$1') + "=([^;]*)"
  ));
  if (matches) {
    const result = decodeURIComponent(matches[1])
    if (result.includes(' auto')) {
      return 'auto'
    }
  }
  return undefined;
}

export function getSavedScheme(): 'light'|'dark'|'auto' {
  return (localStorageProxy.getItem(storageKey) as 'light' | 'dark' | undefined) || (getCookie(storageKey) as 'light' | 'dark' | 'auto') || 'auto'
}

function setDefaultCookie(isAuto: boolean = false) {
  const currentScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  const expires = new Date()
  expires.setFullYear((new Date()).getFullYear() + 1)
  setCookie(storageKey, currentScheme + (isAuto ? ' auto' : ''), { expires })
}

function saveScheme(value: string) {
  const expires = new Date()
  expires.setFullYear((new Date()).getFullYear() + 1)
  setCookie(storageKey, value, { expires })
  try {
    localStorageProxy.setItem(storageKey, value)
  } catch (e) {
    // ignore
  }
}

function clearScheme() {
  setDefaultCookie(true)
  localStorageProxy.removeItem(storageKey)
}

function switchMedia(scheme: string) {
  let lightMedia: string, darkMedia: string

  if (scheme !== 'auto' && scheme === prevScheme) {
    return
  }

  if (scheme === 'auto') {
    lightMedia = '(prefers-color-scheme: light)'
    darkMedia = '(prefers-color-scheme: dark)'
  } else {
    lightMedia = (scheme === 'light') ? 'all' : 'not all'
    darkMedia = (scheme === 'dark') ? 'all' : 'not all'
  }

  lightStyles.forEach(style => {
    if (style.media !== lightMedia) {
      style.media = lightMedia
    }
  })
  darkStyles.forEach(style => {
    if (style.media !== darkMedia) {
      style.media = darkMedia
    }
  })

  prevScheme = scheme
}

function getSystemScheme() {
  const darkScheme = darkSchemeMedia.matches

  return darkScheme ? 'dark' : 'light';
}

function setScheme(scheme: string) {
  switchMedia(scheme)

  if (scheme === 'auto') {
    clearScheme()
  } else {
    saveScheme(scheme)
  }
  hideDropdown()
}

function setupSwitcher() {
  const savedScheme = getSavedScheme();

  const allRadio = document.querySelectorAll<HTMLInputElement>(`[data-plugins*='color-scheme']`)
  allRadio.forEach(element => element.checked = element.value === savedScheme)

  const switchers = document.querySelectorAll<HTMLInputElement>(`[data-plugins*='color-scheme']`)
  switchers.forEach((element: HTMLInputElement & {
    colorSchemeAttached?: boolean
  }) => {
    if (!element.colorSchemeAttached) {
      element.addEventListener('change', (event) => {
        setScheme((event.target as HTMLInputElement).value)
      })
      element.colorSchemeAttached = true
    }
  })
}

export function getCurrentScheme(): 'dark'|'light' {
  let scheme = getSavedScheme();
  if (scheme === 'auto') {
    scheme = getSystemScheme();
  }
  return scheme;
}

function setupScheme() {
  const savedScheme = getSavedScheme()
  const systemScheme = getSystemScheme()

  if (savedScheme === null || savedScheme === 'auto') {
    setDefaultCookie()
    return;
  }

  if (savedScheme !== systemScheme) {
    setScheme(savedScheme)
  }
}

function dropdownOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLDivElement
  if (target.closest('[data-color-scheme-dropdown]')
    || target.hasAttribute('data-color-scheme-dropdown')
    || target.closest('[data-color-scheme-button]')
    || target.hasAttribute('data-color-scheme-button')
  ) {
    return
  }
  hideDropdown();
}

function hideDropdown() {
  dropdown.classList.remove(styles['theme-switcher-dropdown--in'])
  window.removeEventListener('click', dropdownOutsideClick)
}

function showDropdown() {
  window.addEventListener('click', dropdownOutsideClick)
  const button = document.querySelector('[data-color-scheme-button]')
  const btnBound = button.getBoundingClientRect()
  dropdown.style.right = (document.body.clientWidth - btnBound.right - dropdown.clientWidth/2 + btnBound.width/2) + 'px'
  dropdown.classList.add(styles['theme-switcher-dropdown--in'])
}

function buttonEventListener() {
  if (!dropdown.classList.contains(styles['theme-switcher-dropdown--in'])) {
    window.dispatchEvent(new Event('close-all-menus'))
    showDropdown()
    return
  }

  window.dispatchEvent(new Event('close-all-menus'))
}

function attachButton() {
  if (!button.colorSchemeAttached) {
    button.addEventListener('click', buttonEventListener)
    window.addEventListener('close-all-menus', hideDropdown)
    button.colorSchemeAttached = true
  }
}

function addPreferredColorSchemeSwitcher() {
  const darkModePreference = window.matchMedia('(prefers-color-scheme: dark)')
  darkModePreference.addEventListener('change', () => {
    if (getSavedScheme() === 'auto') {
      setScheme('auto')
    }
  })
}

export default () => {
  attachButton()
  setupSwitcher()
  setupScheme()
  addPreferredColorSchemeSwitcher()
}
